/**
 * 017: structure copied from
 * vendor/isomorphic/packages/isomorphic-core/src/components/cards/widget-card.tsx — one card per
 * colliding group, each holding the record links and the "Steelyard does not merge" explanation
 * (non-ecommerce, reason in screen-map §6). Not `order-list/table.tsx` + `expanded-row.tsx`: this
 * report has no table columns to speak of (a group is a list of names and a field, not a row of
 * cells), and neither `MainTable` nor `CustomExpandedComponent` is composed here.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * T025/T026 (010, FR-019/FR-021, SC-006): the Customers-service collisions (§14.4's own dedupe
 * report, FR-069) are the primary source — a failed read here is still this route's own error, via
 * `errorElement`, same as before. The TallySync-service collisions (FR-027) are merged into the
 * *same* list rather than a second screen, and are treated as secondary: a failed Tally read must
 * not take down a report that has real portal-sourced data to show, so it renders inline via
 * `ErrorSurface` instead — the same partial-failure pattern `dashboard.tsx`'s `dashboardLoader` and
 * `integrations/tally/Agent.tsx`'s `agentLoader` already use for an independent secondary read.
 */
import { useLoaderData } from "react-router-dom";
import {
  CustomerDedupeReport,
  parsePortalCollisions,
  parseTallyCollisions,
  type DedupeGroup,
} from "../../components/customers/customer-dedupe-report";
import { getDedupe } from "../../lib/services/customers";
import { getTallyDedupe } from "../../lib/services/tallySync";
import { can } from "../../lib/permissions";
import { loaderPermissions } from "../root";
import { ErrorSurface } from "@steelyard/theme";
import { ListHeader } from "../../components/table/list-header";

export async function dedupeLoader() {
  // Both reports at once: they are independent, and the Tally one is the slow side when TallySync is.
  // Tally's half is `integrations.read`; a role without it (Sales) gets the portal half only, not a 403 panel.
  const readTally = can(loaderPermissions(), "integrations.read");
  const [portalBody, tally] = await Promise.all([
    getDedupe(),
    readTally ? getTallyDedupe().then((body) => ({ body }), (error: unknown) => ({ error })) : { body: null },
  ]);
  const { groups: portalGroups, unreadable: portalUnreadable } = parsePortalCollisions(portalBody);

  let tallyGroups: DedupeGroup[] = [];
  let tallyUnreadable = 0;
  let tallyError: unknown = null;
  try {
    if ("error" in tally) throw tally.error;
    const parsed = readTally ? parseTallyCollisions(tally.body) : { groups: [], unreadable: 0 };
    tallyGroups = parsed.groups;
    tallyUnreadable = parsed.unreadable;
  } catch (err) {
    tallyError = err;
  }

  const groups = [...portalGroups, ...tallyGroups];

  // Both services send each colliding record's name beside its id (parsed into `customerNames`).
  return { groups, unreadable: portalUnreadable + tallyUnreadable, tallyError };
}

export default function CustomersDedupe() {
  const { groups, unreadable, tallyError } = useLoaderData() as Awaited<ReturnType<typeof dedupeLoader>>;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Duplicate Records"
        count={groups.length}
        description="Customer records that collide on GSTIN, registered mobile or normalised legal name — from the portal and from Tally — and records that diverge from the accounting system after a local override."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Customers", href: "/customers" }, { name: "Duplicate Records" }]}
      />
      {tallyError ? <ErrorSurface error={tallyError} action="load Tally-sourced collisions" /> : null}
      <CustomerDedupeReport groups={groups} unreadable={unreadable} />
    </div>
  );
}
